import {
  RenameFolderMediaHouseDocument,
  type RenameFolderMediaHouseMutation,
  type RenameFolderMediaHouseMutationVariables,
} from "@/generated/graphql";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiAlertCircle, FiType, FiX } from "react-icons/fi";

interface RenameFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  currentName: string;
  mediaType: "image" | "video" | "audio";
}

export function RenameFolderModal({
  isOpen,
  onClose,
  folderId,
  currentName,
  mediaType,
}: RenameFolderModalProps) {
  const [folderName, setFolderName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  // Get the correct refetch query based on media type
  const getRefetchQuery = () => {
    if (mediaType === "image") return "GetMediaHouseFolders";
    if (mediaType === "video") return "GetMediaHouseFolders";
    return "GetMediaHouseFolders";
  };

  const [renameFolder, { loading }] = useMutation<
    RenameFolderMediaHouseMutation,
    RenameFolderMediaHouseMutationVariables
  >(RenameFolderMediaHouseDocument, {
    refetchQueries: [getRefetchQuery()],
    onCompleted: () => {
      toast.success("Folder renamed successfully");
      setError(null);
      onClose();
    },
    onError: (err) => {
      setError(err.message);
      toast.error(`Failed to rename folder: ${err.message}`);
    },
  });

  // Update folder name when currentName changes
  useEffect(() => {
    setFolderName(currentName);
  }, [currentName]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFolderName(currentName);
      setError(null);
    }
  }, [isOpen, currentName]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!folderName.trim()) {
      setError("Folder name is required");
      return;
    }

    if (folderName.trim() === currentName) {
      setError("Please enter a different folder name");
      return;
    }

    await renameFolder({
      variables: {
        id: folderId,
        name: folderName.trim(),
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-folder-title"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <FiType className="h-5 w-5 text-gray-600" />
            <h2 id="rename-folder-title" className="h6">
              Rename Folder
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            variant="ghost"
            shape="circle"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={loading}
          >
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="folder-name" className="mb-2 block label">
              Folder Name
            </label>
            <input
              id="folder-name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Enter folder name"
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" onClick={onClose} variant="secondary" disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Renaming..." : "Rename"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
